import { starterScene, type DownloadedScene, type SceneImage } from '../content/scenes';
import type { RewardView, ViewFrame } from '../content/reward-views';

function worldImage(theme: string): string {
  let shapes = '';
  const palette = theme === 'ocean' ? ['#103d54', '#63aab4'] : theme === 'cosmos' ? ['#101b40', '#66539b'] : theme === 'garden' ? ['#315446', '#c38b8e'] : theme === 'kitchen' ? ['#69402e', '#dba765'] : ['#173d34', '#91ac78'];
  if (theme === 'cosmos') {
    shapes = Array.from({ length: 65 }, (_, i) => `<circle cx="${(i * 137) % 1000}" cy="${(i * 97) % 1000}" r="${i % 3 + 1}" fill="#fff1c4"/>`).join('') + '<circle cx="660" cy="350" r="150" fill="#cfb8de"/><ellipse cx="660" cy="350" rx="240" ry="36" fill="none" stroke="#f2d6a4" stroke-width="18" transform="rotate(-25 660 350)"/>';
  } else if (theme === 'ocean') {
    shapes = '<circle cx="730" cy="250" r="110" fill="#ffe3a6"/>' + [420, 550, 690, 830].map((y, i) => `<path d="M0 ${y} Q250 ${y - 130} 500 ${y} T1000 ${y} V1000 H0Z" fill="${['#6eb3b5', '#378b9a', '#226b7e', '#164d63'][i]}"/>`).join('');
  } else if (theme === 'garden') {
    shapes = Array.from({ length: 12 }, (_, i) => { const x = i * 97 % 950 + 25, y = 300 + i * 71 % 550; return `<path d="M${x} 1000 Q${x - 70} ${y + 160} ${x} ${y}" fill="none" stroke="#a6bc81" stroke-width="12"/><g fill="${i % 2 ? '#eac0b3' : '#e4cd83'}">${[0, 72, 144, 216, 288].map(a => `<ellipse cx="${x}" cy="${y - 30}" rx="24" ry="42" transform="rotate(${a} ${x} ${y})"/>`).join('')}</g><circle cx="${x}" cy="${y}" r="18" fill="#ffd77a"/>`; }).join('');
  } else if (theme === 'kitchen') {
    shapes = '<path d="M0 750H1000V1000H0Z" fill="#98653f"/><rect x="140" y="530" width="400" height="210" rx="50" fill="#c1d5bd"/><path d="M100 540H580M210 400Q170 330 230 260M340 400Q300 330 360 260M470 400Q430 330 490 260" fill="none" stroke="#f4deb0" stroke-width="20"/><ellipse cx="760" cy="700" rx="150" ry="60" fill="#eac99a"/><circle cx="730" cy="650" r="60" fill="#bc6951"/><circle cx="815" cy="635" r="55" fill="#dba653"/>';
  } else {
    shapes = '<circle cx="730" cy="250" r="100" fill="#efdc9b"/><path d="M0 650L270 320 500 620 770 390 1000 650V1000H0Z" fill="#6f9275"/>' + [80, 270, 520, 740, 940].map((x, i) => `<path d="M${x} ${460 + i % 2 * 130}l-140 330h90v210h100V790h90Z" fill="${i % 2 ? '#234f40' : '#315f49'}"/>`).join('');
  }
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><defs><linearGradient id="sky" x2="0" y2="1"><stop stop-color="${palette[0]}"/><stop offset="1" stop-color="${palette[1]}"/></linearGradient></defs><path fill="url(#sky)" d="M0 0H1000V1000H0Z"/>${shapes}</svg>`);
}

const worlds = new Map<string, string>();
async function prepareImage(src: string, expected?: SceneImage): Promise<boolean> {
  const image = new Image();
  let timer = 0;
  try {
    image.src = src;
    return await Promise.race([
      image.decode().then(() => !expected || image.naturalWidth === expected.width && image.naturalHeight === expected.height),
      new Promise<false>(resolve => { timer = window.setTimeout(() => resolve(false), 1200); }),
    ]);
  } catch { return false; }
  finally { clearTimeout(timer); image.removeAttribute('src'); }
}
export class Scenery {
  private timer = 0;
  private finish?: (dismissed: boolean) => void;
  private dismissing = false;
  private start?: { x: number; y: number; id: number };
  private image = '';
  private title = '';
  private theme = '';
  private custom?: string | DownloadedScene;
  private view?: RewardView;
  private requested = '';
  private assetEpoch = 0;
  private presentationEpoch = 0;
  private ready: Promise<void> = Promise.resolve();
  private wide = matchMedia('(min-aspect-ratio: 1/1)');
  constructor(private background: HTMLImageElement, private dialog: HTMLDialogElement) {
    this.wide.addEventListener('change', () => this.updateImages());
    dialog.addEventListener('close', () => { if (!dialog.open && this.finish) this.cancel(true); });
    dialog.addEventListener('contextmenu', event => event.preventDefault());
    dialog.querySelector('button')!.addEventListener('click', () => { void this.dismiss(); });
    dialog.addEventListener('cancel', event => { event.preventDefault(); void this.dismiss(); });
    dialog.addEventListener('pointerdown', event => {
      if (dialog.dataset.phase !== 'reveal' || !event.isPrimary || event.button !== 0 || (event.target as Element).closest('button')) return;
      this.start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      dialog.setPointerCapture(event.pointerId);
    });
    dialog.addEventListener('pointerup', event => {
      const start = this.start; this.start = undefined;
      if (start?.id === event.pointerId && Math.hypot(event.clientX - start.x, event.clientY - start.y) >= 60) void this.dismiss();
    });
    dialog.addEventListener('pointercancel', () => { this.start = undefined; });
  }
  setWorld(theme: string, title: string, completed: boolean, custom?: string | DownloadedScene, view?: RewardView): void {
    if (!worlds.has(theme)) worlds.set(theme, worldImage(theme));
    this.theme = theme; this.custom = custom; this.title = title; this.view = view;
    this.updateImages();
    document.body.toggleAttribute('data-world-completed', completed);
  }
  private updateImages(): void {
    if (!this.theme) return;
    const scene = starterScene(this.theme);
    const rendition = scene?.[this.wide.matches ? 'landscape' : 'portrait'];
    const fallback = worlds.get(this.theme)!;
    const custom = typeof this.custom === 'string' ? { src: this.custom, focal: [.5, .5] } : this.custom?.[this.wide.matches ? 'landscape' : 'portrait'];
    const next = custom?.src || rendition?.src || fallback;
    const knownScene = !this.custom || typeof this.custom !== 'string' && this.custom.sceneId === scene?.id;
    const framing = knownScene && this.view?.id !== 'whole' ? this.view?.[this.wide.matches ? 'landscape' : 'portrait'] : undefined;
    const viewId = knownScene ? this.view?.id ?? 'whole' : 'whole';
    const key = `${this.theme}:${next}:${viewId}`;
    if (key === this.requested) return;
    const themeChanged = !this.requested.startsWith(`${this.theme}:`);
    this.requested = key;
    const epoch = ++this.assetEpoch;
    const focal = framing?.focal ?? custom?.focal ?? rendition?.focal ?? [.5, .5];
    const position = `${focal[0]! * 100}% ${focal[1]! * 100}%`;
    // Keep the current composition on resize; new themes start with a local vector.
    if (!this.image || themeChanged) this.activate(fallback, '50% 50%');
    this.ready = next === fallback ? Promise.resolve() : prepareImage(next, this.custom ? undefined : rendition).then(valid => {
      if (epoch === this.assetEpoch && valid) this.activate(next, position, framing, viewId);
    });
  }
  private activate(src: string, position: string, framing?: ViewFrame, viewId = 'whole'): void {
    this.image = src;
    const preview = this.dialog.querySelector<HTMLImageElement>('img')!;
    this.background.src = src; preview.src = src;
    this.background.style.objectPosition = position; preview.style.objectPosition = position;
    const zoom = framing?.zoom ?? 1, focal = framing?.focal ?? [.5, .5];
    const transform = `translate(${(zoom - 1) * (.5 - focal[0]!) * 100}%, ${(zoom - 1) * (.5 - focal[1]!) * 100}%) scale(${zoom})`;
    for (const image of [this.background, preview]) {
      image.style.transformOrigin = '50% 50%'; image.style.transform = transform;
      image.dataset.rewardView = viewId;
    }
  }
  async present(phase: 'preview' | 'reveal', action: string, prompt = ''): Promise<boolean> {
    this.cancel();
    const epoch = this.presentationEpoch;
    await this.ready;
    if (epoch !== this.presentationEpoch) return false;
    this.dialog.querySelector<HTMLImageElement>('img')!.src = this.image;
    this.dialog.querySelector('h2')!.textContent = this.title;
    this.dialog.querySelector('button')!.textContent = action;
    const message = this.dialog.querySelector<HTMLElement>('.artwork-prompt')!;
    message.textContent = prompt;
    message.hidden = phase !== 'reveal';
    this.dialog.dataset.phase = phase;
    return new Promise<boolean>(resolve => {
      this.finish = resolve; this.dialog.showModal();
      if (phase === 'preview') this.timer = window.setTimeout(() => this.cancel(), 1100);
      else {
        const reduced = document.documentElement.hasAttribute('data-reduced-motion') || matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.timer = window.setTimeout(() => { message.hidden = true; }, reduced ? 3000 : 3600);
      }
    });
  }
  private async dismiss(): Promise<void> {
    if (!this.dialog.open || this.dismissing) return;
    this.dismissing = true;
    const finish = this.finish;
    if (this.dialog.dataset.phase === 'reveal' && !document.documentElement.hasAttribute('data-reduced-motion') && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      await this.dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220 }).finished.catch(() => {});
    }
    if (this.finish === finish) this.cancel(true);
    this.dismissing = false;
  }
  cancel(dismissed = false): void { this.presentationEpoch++; clearTimeout(this.timer); this.start = undefined; const done = this.finish; this.finish = undefined; if (this.dialog.open) this.dialog.close(); done?.(dismissed); }
}
